import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

function EventsAndMore() {
  const { isAdmin } = useAuth();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [detailWorkshop, setDetailWorkshop] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    participants: 1,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [expandedPastEvent, setExpandedPastEvent] = useState(null);
  const [eventImages, setEventImages] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const fileInputRefKiAfterwork = useRef(null);

  useEffect(() => {
    fetchWorkshops();
    fetchEventImages('past_event', 'schnitzeljagd-2026');
    fetchEventImages('past_event', 'ki-afterwork-2026');
  }, []);

  const fetchEventImages = (eventType, eventKey) => {
    fetch(`/api/event-images/${eventType}/${eventKey}`)
      .then(res => res.json())
      .then(data => {
        if (data.data) {
          setEventImages(prev => ({ ...prev, [`${eventType}-${eventKey}`]: data.data }));
        }
      })
      .catch(err => console.error('Fehler beim Laden der Bilder:', err));
  };

  const handleImageUpload = async (eventType, eventKey, file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/event-images/${eventType}/${eventKey}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.message === 'success') {
        fetchEventImages(eventType, eventKey);
      } else {
        alert('Fehler: ' + (data.error || 'Upload fehlgeschlagen'));
      }
    } catch (err) {
      alert('Fehler beim Upload: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imageId, eventType, eventKey) => {
    if (!window.confirm('Bild wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/event-images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchEventImages(eventType, eventKey);
      }
    } catch (err) {
      alert('Fehler beim Löschen: ' + err.message);
    }
  };

  const fetchWorkshops = () => {
    setLoading(true);
    fetch('/api/public/workshops')
      .then(res => res.json())
      .then(data => {
        setWorkshops(data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Fehler:', err);
        setLoading(false);
      });
  };

  const handleBooking = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    fetch(`/api/public/workshops/${selectedWorkshop.id}/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingForm)
    })
      .then(res => res.json())
      .then(data => {
        setSubmitting(false);
        if (data.message === 'success') {
          setSuccess(data.data);
          setSelectedWorkshop(null);
          setBookingForm({
            name: '',
            email: '',
            phone: '',
            participants: 1,
            message: ''
          });
          fetchWorkshops();
        } else {
          setError(data.error || 'Fehler bei der Buchung');
        }
      })
      .catch(err => {
        setSubmitting(false);
        setError('Verbindungsfehler');
      });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr, duration) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const endMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    return `${timeStr.substring(0, 5)} - ${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')} Uhr`;
  };

  const formatPrice = (price) => {
    if (!price || parseFloat(price) === 0) return null;
    return parseFloat(price).toLocaleString('de-DE', { minimumFractionDigits: 2 }) + ' EUR';
  };

  const hasTieredPricing = (workshop) => {
    return workshop.price_sibling || workshop.price_member || workshop.price_member_sibling;
  };

  const renderPriceTable = (workshop) => {
    if (!hasTieredPricing(workshop)) {
      return workshop.price > 0 ? formatPrice(workshop.price) : 'Kostenlos';
    }
    return (
      <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.9rem' }}>
        <thead className="table-light">
          <tr>
            <th></th>
            <th>1. Kind</th>
            {workshop.price_sibling && <th>Geschwister</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Normal</strong></td>
            <td>{formatPrice(workshop.price)}</td>
            {workshop.price_sibling && <td>{formatPrice(workshop.price_sibling)}</td>}
          </tr>
          {(workshop.price_member || workshop.price_member_sibling) && (
            <tr>
              <td><strong>Mitglieder</strong></td>
              <td>{formatPrice(workshop.price_member)}</td>
              {workshop.price_sibling && <td>{formatPrice(workshop.price_member_sibling)}</td>}
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  // Success view
  if (success) {
    return (
      <div>
        <div className="card border-success">
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
              </svg>
            </div>
            <h2 className="text-success mb-3">Anmeldung erfolgreich!</h2>
            <p className="lead mb-4">
              Vielen Dank, {success.name}!<br />
              Deine Anmeldung wurde bestätigt.
            </p>
            <div className="bg-light rounded p-3 mb-4">
              <strong>{success.workshop_title}</strong><br />
              {formatDate(success.workshop_date)}<br />
              {success.workshop_time} Uhr
            </div>
            <p className="text-muted mb-4">
              Eine Bestätigung wird an <strong>{success.email}</strong> gesendet.
            </p>
            <button
              className="btn btn-outline-primary"
              onClick={() => setSuccess(null)}
            >
              Weitere Events ansehen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1>Workshops & Events</h1>
        <p className="text-muted">
          Entdecke unsere Workshops und Events für Kinder und Erwachsene!
        </p>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : selectedWorkshop ? (
          /* Booking Form */
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Anmeldung</h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-light border mb-4">
                    <h5>{selectedWorkshop.title}</h5>
                    <strong>{formatDate(selectedWorkshop.date)}</strong><br />
                    {formatTime(selectedWorkshop.time, selectedWorkshop.duration)}<br />
                    {selectedWorkshop.location && <span className="text-muted">{selectedWorkshop.location}</span>}
                    {selectedWorkshop.price > 0 && (
                      <div className="mt-3">
                        <strong>Preise:</strong>
                        <div className="mt-2">{renderPriceTable(selectedWorkshop)}</div>
                      </div>
                    )}
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}

                  <form onSubmit={handleBooking}>
                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={bookingForm.name}
                        onChange={e => setBookingForm({ ...bookingForm, name: e.target.value })}
                        required
                        placeholder="Vor- und Nachname"
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">E-Mail *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={bookingForm.email}
                          onChange={e => setBookingForm({ ...bookingForm, email: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Telefon</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={bookingForm.phone}
                          onChange={e => setBookingForm({ ...bookingForm, phone: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Anzahl Teilnehmer</label>
                      <input
                        type="number"
                        className="form-control"
                        value={bookingForm.participants}
                        onChange={e => setBookingForm({ ...bookingForm, participants: parseInt(e.target.value) })}
                        min="1"
                        max={selectedWorkshop.max_participants - (selectedWorkshop.booked_count || 0)}
                      />
                      <small className="text-muted">
                        Noch {selectedWorkshop.max_participants - (selectedWorkshop.booked_count || 0)} Plätze frei
                      </small>
                    </div>

                    <div className="mb-4">
                      <label className="form-label">Nachricht / Fragen</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={bookingForm.message}
                        onChange={e => setBookingForm({ ...bookingForm, message: e.target.value })}
                      />
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg flex-grow-1"
                        disabled={submitting}
                      >
                        {submitting ? 'Wird gebucht...' : 'Verbindlich anmelden'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-lg"
                        onClick={() => setSelectedWorkshop(null)}
                        disabled={submitting}
                      >
                        Zurück
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : workshops.length === 0 ? (
          <div className="text-center py-5">
            <div className="card">
              <div className="card-body py-5">
                <h4>Aktuell keine Events geplant</h4>
                <p className="text-muted mb-4">
                  Schau bald wieder vorbei oder kontaktiere uns für individuelle Anfragen.
                </p>
                <a href="mailto:info@futurhinos-kids.de" className="btn btn-primary">
                  E-Mail senden
                </a>
              </div>
            </div>
          </div>
        ) : (
          /* Workshop List */
          <div className="row">
            {workshops.map(workshop => {
              const spotsLeft = workshop.max_participants - (workshop.booked_count || 0);
              const isFull = !workshop.open_event && spotsLeft <= 0;

              return (
                <div key={workshop.id} className="col-md-6 col-lg-4 mb-4">
                  <div className={`card h-100 ${isFull ? 'opacity-75' : ''}`}>
                    <div className="card-body">
                      <h5 className="card-title">{workshop.title}</h5>
                      <div className="mb-2">
                        <span className="badge bg-primary me-2">
                          {workshop.dates_display || new Date(workshop.date).toLocaleDateString('de-DE', {
                            weekday: 'short',
                            day: '2-digit',
                            month: '2-digit'
                          })}
                        </span>
                        <span className="text-muted me-2">
                          {workshop.time?.substring(0, 5)} Uhr
                        </span>
                        <span className={`badge ${workshop.target_audience === 'kinder' ? 'bg-warning text-dark' : workshop.target_audience === 'alle' ? 'bg-success' : 'bg-secondary'}`}>
                          {workshop.target_audience === 'kinder' ? 'Kinder' : workshop.target_audience === 'alle' ? 'Alle' : 'Erwachsene'}
                        </span>
                      </div>
                      {workshop.description && (
                        <p className="card-text small text-muted mb-2">
                          {workshop.description.substring(0, 120)}
                          {workshop.description.length > 120 && '...'}
                        </p>
                      )}
                      <button
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => setDetailWorkshop(workshop)}
                      >
                        Mehr erfahren &rarr;
                      </button>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          {workshop.price > 0 ? (
                            <span className="fw-bold">
                              {hasTieredPricing(workshop) ? 'ab ' : ''}{formatPrice(workshop.price_member_sibling || workshop.price_member || workshop.price_sibling || workshop.price)}
                            </span>
                          ) : (
                            <span className="badge bg-success">Kostenlos</span>
                          )}
                        </div>
                        {workshop.open_event ? (
                          <span className="badge bg-info">Ohne Anmeldung</span>
                        ) : (
                          <span className={`badge ${isFull ? 'bg-danger' : 'bg-info'}`}>
                            {isFull ? 'Ausgebucht' : `${spotsLeft} Plätze frei`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="card-footer bg-transparent">
                      {workshop.open_event ? (
                        <div className="text-center text-success fw-bold py-2">
                          Einfach vorbeikommen!
                        </div>
                      ) : (
                        <button
                          className="btn btn-primary w-100"
                          onClick={() => setSelectedWorkshop(workshop)}
                          disabled={isFull}
                        >
                          {isFull ? 'Ausgebucht' : 'Jetzt anmelden'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Vergangene Veranstaltungen */}
      <div className="mt-5 pt-4 border-top">
        <h2 className="mb-4">Vergangene Veranstaltungen</h2>

        {/* Osterschnitzeljagd 2026 */}
        <div className="card mb-3" style={{ overflow: 'hidden' }}>
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{
              background: 'linear-gradient(135deg, #e8f5e9, #fff3e0)',
              cursor: 'pointer',
              borderLeft: '4px solid #00d084'
            }}
            onClick={() => setExpandedPastEvent(expandedPastEvent === 'schnitzeljagd' ? null : 'schnitzeljagd')}
          >
            <div>
              <h5 className="mb-0" style={{ color: '#2e7d32' }}>
                <span style={{ marginRight: '8px' }}>🐰</span>
                Osterschnitzeljagd 2026
              </h5>
              <small className="text-muted">30. März - 02. April 2026</small>
            </div>
            <div className="d-flex align-items-center">
              <span className="badge bg-success me-3">70+ Familien</span>
              <span style={{
                transform: expandedPastEvent === 'schnitzeljagd' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                fontSize: '1.2rem'
              }}>
                ▼
              </span>
            </div>
          </div>

          {expandedPastEvent === 'schnitzeljagd' && (
            <div className="card-body" style={{ background: '#fafafa' }}>
              <div className="row">
                <div className="col-lg-8">
                  <p className="lead" style={{ color: '#2e7d32' }}>
                    Über 70 Familien haben an unserer Osterschnitzeljagd teilgenommen!
                  </p>
                  <p>
                    Es war ein großer Spaß für alle Beteiligten und Teilnehmenden. Bei strahlendem Frühlingswetter
                    machten sich kleine und große Entdecker auf den Weg durch Schorndorf, um knifflige Rätsel zu lösen
                    und versteckte Hinweise zu finden.
                  </p>
                  <p>
                    Die Kinder waren mit Feuereifer dabei und haben bewiesen, dass Teamwork und Cleverness
                    zum Erfolg führen. Am Ende warteten tolle Preise auf alle erfolgreichen Schatzsucher.
                  </p>
                  <p className="mb-0">
                    <strong>Vielen Dank an alle Teilnehmer und Helfer!</strong> Wir freuen uns schon auf das nächste Jahr.
                  </p>
                </div>
                <div className="col-lg-4">
                  <div className="bg-white rounded p-3 text-center shadow-sm">
                    <div style={{ fontSize: '3rem' }}>🐰🥚🎉</div>
                    <div className="mt-2">
                      <span className="badge bg-success me-2">70+ Familien</span>
                      <span className="badge bg-info">4 Tage</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bildergalerie */}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Impressionen</h6>
                  {isAdmin && (
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageUpload('past_event', 'schnitzeljagd-2026', e.target.files[0])}
                      />
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? 'Lädt...' : '+ Bild hinzufügen'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="row g-2">
                  {(eventImages['past_event-schnitzeljagd-2026'] || []).map(img => (
                    <div key={img.id} className="col-6 col-md-4 col-lg-3">
                      <div className="position-relative">
                        <img
                          src={`/api/event-images/image/${img.id}`}
                          alt={img.caption || img.filename}
                          className="img-fluid rounded"
                          style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                        />
                        {isAdmin && (
                          <button
                            className="btn btn-danger btn-sm position-absolute"
                            style={{ top: '5px', right: '5px', padding: '2px 6px', fontSize: '0.7rem' }}
                            onClick={() => handleDeleteImage(img.id, 'past_event', 'schnitzeljagd-2026')}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!eventImages['past_event-schnitzeljagd-2026'] || eventImages['past_event-schnitzeljagd-2026'].length === 0) && (
                    <div className="col-6 col-md-4 col-lg-3">
                      <div className="ratio ratio-4x3 bg-white rounded d-flex align-items-center justify-content-center" style={{ border: '2px dashed #ddd' }}>
                        <div className="text-center text-muted p-3">
                          <div style={{ fontSize: '2rem' }}>📸</div>
                          <small>{isAdmin ? 'Klicke oben auf "+ Bild hinzufügen"' : 'Bilder folgen'}</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* KI & Afterwork */}
        <div className="card mb-3" style={{ overflow: 'hidden' }}>
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{
              background: 'linear-gradient(135deg, #fce4ec, #ede7f6)',
              cursor: 'pointer',
              borderLeft: '4px solid #f810b1'
            }}
            onClick={() => setExpandedPastEvent(expandedPastEvent === 'ki-afterwork' ? null : 'ki-afterwork')}
          >
            <div>
              <h5 className="mb-0" style={{ color: '#9b59b6' }}>
                <span style={{ marginRight: '8px' }}>🤖</span>
                KI &amp; Afterwork
              </h5>
              <small className="text-muted">2026</small>
            </div>
            <div className="d-flex align-items-center">
              <span className="badge me-3" style={{ background: '#f810b1' }}>Präsentation</span>
              <span style={{
                transform: expandedPastEvent === 'ki-afterwork' ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                fontSize: '1.2rem'
              }}>
                ▼
              </span>
            </div>
          </div>

          {expandedPastEvent === 'ki-afterwork' && (
            <div className="card-body" style={{ background: '#fafafa' }}>
              <div className="row">
                <div className="col-lg-8">
                  <p className="lead" style={{ color: '#9b59b6' }}>
                    Ein entspannter Abend rund um Künstliche Intelligenz.
                  </p>
                  <p>
                    Bei unserem KI &amp; Afterwork haben wir gemeinsam einen Blick auf aktuelle KI-Tools geworfen,
                    Anwendungsmöglichkeiten im Alltag diskutiert und ausprobiert, wie KI auch im Umfeld von FutuRhinos
                    sinnvoll eingesetzt werden kann.
                  </p>
                  <p className="mb-0">
                    Die komplette Präsentation des Abends findest du weiter unten zum Anschauen und Herunterladen.
                  </p>
                </div>
                <div className="col-lg-4">
                  <div className="bg-white rounded p-3 text-center shadow-sm">
                    <div style={{ fontSize: '3rem' }}>🤖✨💡</div>
                    <div className="mt-2">
                      <span className="badge" style={{ background: '#9b59b6' }}>Präsentation unten</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bildergalerie */}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Impressionen</h6>
                  {isAdmin && (
                    <div>
                      <input
                        type="file"
                        ref={fileInputRefKiAfterwork}
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageUpload('past_event', 'ki-afterwork-2026', e.target.files[0])}
                      />
                      <button
                        className="btn btn-sm"
                        style={{ background: '#f810b1', color: 'white' }}
                        onClick={() => fileInputRefKiAfterwork.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? 'Lädt...' : '+ Bild hinzufügen'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="row g-2">
                  {(eventImages['past_event-ki-afterwork-2026'] || []).map(img => (
                    <div key={img.id} className="col-6 col-md-4 col-lg-3">
                      <div className="position-relative">
                        <img
                          src={`/api/event-images/image/${img.id}`}
                          alt={img.caption || img.filename}
                          className="img-fluid rounded"
                          style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                        />
                        {isAdmin && (
                          <button
                            className="btn btn-danger btn-sm position-absolute"
                            style={{ top: '5px', right: '5px', padding: '2px 6px', fontSize: '0.7rem' }}
                            onClick={() => handleDeleteImage(img.id, 'past_event', 'ki-afterwork-2026')}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!eventImages['past_event-ki-afterwork-2026'] || eventImages['past_event-ki-afterwork-2026'].length === 0) && (
                    <div className="col-6 col-md-4 col-lg-3">
                      <div className="ratio ratio-4x3 bg-white rounded d-flex align-items-center justify-content-center" style={{ border: '2px dashed #ddd' }}>
                        <div className="text-center text-muted p-3">
                          <div style={{ fontSize: '2rem' }}>📸</div>
                          <small>{isAdmin ? 'Klicke oben auf "+ Bild hinzufügen"' : 'Bilder folgen'}</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Präsentation */}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Präsentation</h6>
                  <a
                    href="/ki-afterwork.pdf"
                    download
                    className="btn btn-sm btn-outline-secondary"
                  >
                    PDF herunterladen
                  </a>
                </div>
                <div style={{ width: '100%', height: '70vh', background: 'white', borderRadius: '4px', overflow: 'hidden' }}>
                  <iframe
                    src="/ki-afterwork.pdf"
                    title="KI & Afterwork Präsentation"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  >
                    <p>
                      Dein Browser kann das PDF nicht anzeigen.{' '}
                      <a href="/ki-afterwork.pdf">Hier herunterladen.</a>
                    </p>
                  </iframe>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detailWorkshop && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setDetailWorkshop(null)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{detailWorkshop.title}</h5>
                <button type="button" className="btn-close" onClick={() => setDetailWorkshop(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-sm-6">
                    <strong>Datum:</strong><br />
                    {formatDate(detailWorkshop.date)}
                  </div>
                  <div className="col-sm-6">
                    <strong>Uhrzeit:</strong><br />
                    {formatTime(detailWorkshop.time, detailWorkshop.duration)}
                  </div>
                </div>

                {detailWorkshop.location && (
                  <div className="mb-3">
                    <strong>Ort:</strong><br />
                    {detailWorkshop.location}
                  </div>
                )}

                <div className="mb-3">
                  <strong>Zielgruppe:</strong><br />
                  {detailWorkshop.target_audience === 'kinder' ? 'Kinder' : detailWorkshop.target_audience === 'alle' ? 'Alle' : 'Erwachsene'}
                </div>

                <div className="mb-3">
                  <strong>Preis:</strong>
                  <div className="mt-2">{renderPriceTable(detailWorkshop)}</div>
                </div>

                {!detailWorkshop.open_event && (
                  <div className="mb-3">
                    <strong>Verfügbare Plätze:</strong><br />
                    {detailWorkshop.max_participants - (detailWorkshop.booked_count || 0)} von {detailWorkshop.max_participants}
                  </div>
                )}

                {detailWorkshop.description && (
                  <div className="mb-3">
                    <strong>Beschreibung:</strong>
                    <p className="mt-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{detailWorkshop.description}</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDetailWorkshop(null)}>
                  Schließen
                </button>
                {detailWorkshop.open_event ? (
                  <span className="btn btn-success disabled">Ohne Anmeldung - Einfach vorbeikommen!</span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setDetailWorkshop(null);
                      setSelectedWorkshop(detailWorkshop);
                    }}
                    disabled={detailWorkshop.max_participants - (detailWorkshop.booked_count || 0) <= 0}
                  >
                    {detailWorkshop.max_participants - (detailWorkshop.booked_count || 0) <= 0 ? 'Ausgebucht' : 'Jetzt anmelden'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventsAndMore;
